/* eslint-disable import/extensions,import/no-unresolved,import/no-duplicates */

/**
 * Maps dynamic payment form values (keyed by API FieldName) to the claim contract fields.
 * Mirrors the same logic used in stepAppsureBankDetailsDataMapper.
 */
function mapDynamicBankFields(dynamicFormValues, dynamicPaymentFormFields) {
  if (!dynamicFormValues || Object.keys(dynamicFormValues).length === 0) {
    return {};
  }

  const fieldNames = (dynamicPaymentFormFields || []).map((f) => f.fieldName);

  // Derive primary bank code type from the field the DB flags as IsCodeCheck (TA-4751/TA-4779) -
  // field presence alone can't tell SWIFT/IBAN/BankBranchCode apart since all three may coexist on a form.
  const codeCheckField = (dynamicPaymentFormFields || []).find((f) => f.isCodeCheck === true);
  let bankCodeType = null;
  if (codeCheckField) {
    if (codeCheckField.fieldName === 'SwiftCode') bankCodeType = 'SWIFT Code';
    else if (codeCheckField.fieldName === 'IBAN') bankCodeType = 'IBAN';
    else if (codeCheckField.fieldName === 'BankBranchCode') bankCodeType = 'Bank Code';
  }
  if (!bankCodeType) {
    if (fieldNames.includes('SwiftCode')) bankCodeType = 'SWIFT Code';
    else if (fieldNames.includes('IBAN')) bankCodeType = 'IBAN';
    else if (fieldNames.includes('BankBranchCode')) bankCodeType = 'Bank Code';
  }

  const swiftCode = dynamicFormValues.SwiftCode || '';
  const iban = dynamicFormValues.IBAN || '';
  const bankBranchCode = dynamicFormValues.BankBranchCode || '';

  // Keep an explicitly entered branch code; only fall back to SWIFT/IBAN when none was provided.
  let resolvedBankBranchCode = bankBranchCode;
  if (!bankBranchCode && bankCodeType === 'SWIFT Code') resolvedBankBranchCode = swiftCode;
  else if (!bankBranchCode && bankCodeType === 'IBAN') resolvedBankBranchCode = iban;

  return {
    ...(fieldNames.includes('BankName') && { bankName: dynamicFormValues.BankName || '' }),
    bankBranchCode: resolvedBankBranchCode,
    ...(fieldNames.includes('BankBranchName') && { bankBranchName: dynamicFormValues.BankBranchName || '' }),
    ...(fieldNames.includes('BankBranchCode') && { bankRoutingNumber: bankBranchCode }),
    ...(fieldNames.includes('BankAccountName') && { bankAccountName: dynamicFormValues.BankAccountName || '' }),
    ...(fieldNames.includes('BankAccountNumber') && { bankAccountNumber: dynamicFormValues.BankAccountNumber || '' }),
    ...(fieldNames.includes('BankAccountType') && { bankAccountType: dynamicFormValues.BankAccountType === undefined ? null : dynamicFormValues.BankAccountType }),
    ...(bankCodeType && { bankCodeType }),
    ...(fieldNames.includes('BankAddress') && { bankAddress: dynamicFormValues.BankAddress || '' }),
    ...(fieldNames.includes('BankPostCode') && { bankPostcode: dynamicFormValues.BankPostCode || '' }),
    ...(fieldNames.includes('BankSuburb') && { bankSuburbOrCity: dynamicFormValues.BankSuburb || '' }),
    ...(fieldNames.includes('IBAN') && { IBAN: iban || null }),
    ...(fieldNames.includes('SwiftCode') && { swiftCode: swiftCode || null }),
    ...(fieldNames.includes('RecipientReference') && { recipientReference: dynamicFormValues.RecipientReference || null }),
    ...(fieldNames.includes('PaymentCode') && { paymentCode: dynamicFormValues.PaymentCode || null }),
    ...(fieldNames.includes('IdentificationNumber') && { identificationNumber: dynamicFormValues.IdentificationNumber || null }),
    ...(fieldNames.includes('IsInvalidBsb') && { invalidBSB: !!dynamicFormValues.IsInvalidBsb }),
    ...(fieldNames.includes('Email') && { email: dynamicFormValues.Email || null }),
    ...(fieldNames.includes('PaymentPurpose') && { PaymentPurpose: dynamicFormValues.PaymentPurpose || null }),
  };
}

/**
 * Serializes the dynamic payment form values (keyed by API FieldName) into a JSON string.
 * Mirrors the backend MapCountryPaymentFields which stores jsonData[FieldName] = Value.
 */
function buildCreditorJsonData(dynamicFormValues, dynamicPaymentFormFields) {
  if (!dynamicPaymentFormFields || dynamicPaymentFormFields.length === 0) {
    return null;
  }

  const values = dynamicFormValues || {};
  const jsonData = {};

  dynamicPaymentFormFields.forEach((field) => {
    const key = field.fieldName;
    if (!key) return;
    const rawValue = values[key];
    jsonData[key] = key === 'IsInvalidBsb' ? !!rawValue : rawValue !== undefined ? rawValue : null;
  });

  return Object.keys(jsonData).length > 0 ? JSON.stringify(jsonData) : null;
}

function mapToParentSection(data) {
  const countryDetails = !!data.country && typeof data.country.toJS === 'function' ? data.country.toJS() : data.country;
  const hasDynamicForm = data.dynamicPaymentFormFields && data.dynamicPaymentFormFields.length > 0;

  let bankFields;
  if (hasDynamicForm) {
    bankFields = mapDynamicBankFields(data.dynamicFormValues, data.dynamicPaymentFormFields);
  } else {
    bankFields = {
      bankName: data.bank,
      bankBranchCode: data.bankTypeCode === 'SWIFT Code' ? data.swiftCode : data.bankTypeCode === 'Bank Code' ? data.bankCodeOrRoutingNumber : '',
      bankBranchName: data.bankBranch,
      bankRoutingNumber: data.bankCodeOrRoutingNumber,
      bankAccountName: data.bankAccountName,
      bankAccountNumber: data.bankAccountNumber,
      bankCodeType: data.bankTypeCode,
      bankAddress: data.bankAddress,
      bankPostcode: data.bankPostcode,
      bankSuburbOrCity: data.bankSuburbOrCity,
      IBAN: data.bankTypeCode === 'IBAN' ? data.iban : null,
    };
  }

  const creditorJsonData = hasDynamicForm
    ? buildCreditorJsonData(data.dynamicFormValues, data.dynamicPaymentFormFields)
    : null;

  return {
    ...bankFields,
    ...(creditorJsonData && { CreditorJsonData: creditorJsonData }),
    creditorName: data.firstName,
    creditorSurname: data.lastName,
    creditorFullName: `${data.firstName} ${data.lastName}`,
    physicalAddress1: data.addressLine1,
    physicalAddress2: data.addressLine2,
    npi: null,
    federalTaxId: null,
    titleID: data.title,
    country: countryDetails ? countryDetails.id : null,
    cellNumber: data.mobile,
    physicalAddress3: data.city,
    physicalAddress4: data.state,
    physicalCode: data.postalZipCode,
    creditorExtension: {
      payToEntity: data.paymentTo,
      paymentVia: data.paymentVia,
    },
    fBankCountry: countryDetails ? countryDetails.id : null,
  };
}

function mapToQuestionnaire() {
  return [];
}

function mapToExpenses() {
  return [];
}

export default {
  mapToParentSection,
  mapToQuestionnaire,
  mapToExpenses,
};



// WEBPACK FOOTER //
// ./app/containers/FastCover/dataMapper/stepPaymentDetailsDataMapper.js